import React, { useContext, useEffect, useState } from "react";
import { Form, Input, Button, Alert, Radio, Spin, Checkbox, Select, Row, Col, message } from "antd";
import api from "../../services/Api";
import { AuthContext } from "../../context/AuthContext";

const ProfileForm = ({ onSaveSuccess, isRegistration = false }) => {
    const { isAuthenticated, user } = useContext(AuthContext);
    const [form] = Form.useForm();
    const [showCompanyFields, setShowCompanyFields] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [registrationSuccess, setRegistrationSuccess] = useState(false);

    const defaultCountry = "Suomi";

    useEffect(() => {
        // Update form fields with the latest user data whenever `user` changes
        if (user) {
            form.setFieldsValue({
                ...user,
                country: user.country || defaultCountry,
                type: user.type || "person",
            });
            setShowCompanyFields(user?.type === "company");
        } else {
            // Registration: ensure a sensible default is visible
            form.setFieldsValue({ country: defaultCountry });
            setShowCompanyFields(false);
        }
    }, [user, form]);

    const onFinish = async (values) => {
        setLoading(true);

        try {
            await api.saveUser(values);
            setError(false);

            // Show appropriate success message based on context
            if (!isAuthenticated) {
                // Registration success - show success message and set detailed info state
                message.success("Rekisteröityminen onnistui! Saat pian vahvistussähköpostin.");
                setRegistrationSuccess(true);
                
                // If this is a modal registration, don't close the modal
                // If it's a page registration, the parent will handle hiding the form
                if (!isRegistration) {
                    onSaveSuccess();
                }
            } else {
                // Profile update success - show simple message
                message.success("Tiedot tallennettu");
                onSaveSuccess(); // Update the parent component's user state
            }
        } catch (error) {
            setError("Virhe tietojen tallentamisessa. " + (error.response?.data?.message || error.message));
        } finally {
            setLoading(false);
        }
    };

    const onUserTypeChange = (e) => {
        setShowCompanyFields(e.target.value === "company");
    };

    return (
        <>
            {error && <Alert showIcon className="mb-5" message={error} type="error" />}
            {registrationSuccess && !isAuthenticated ? (
                <Alert
                    showIcon
                    className="mb-5"
                    message="Rekisteröityminen onnistui!"
                    description={
                        <div>
                            <p>Saat pian sähköpostiisi vahvistusviestin, jossa on ohjeet tilisi vahvistamiseen. Vahvistamisen jälkeen voit kirjautua sisään.</p>
                            <p>Jos et saa viestiä, tarkista roskapostikansio tai ota yhteyttä{" "}
                                <a href="/yhteystiedot">asiakaspalveluun</a>.</p>
                        </div>
                    }
                    type="success"
                />
            ) : loading ? (
                <Spin />
            ) : (
                <Form
                    form={form}
                    name="profile"
                    onFinish={onFinish}
                    layout="vertical"
                    size="small"
                    className="lh-compact-form"
                    initialValues={{ country: defaultCountry, ...(user || {}) }}
                >
                    <Row gutter={[12, 0]}>
                        <Col xs={24} md={12}>
                            <Form.Item
                                label="Etunimi"
                                name="firstname"
                                rules={[{ required: true, message: "Etunimi on pakollinen." }]}
                            >
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item
                                label="Sukunimi"
                                name="lastname"
                                rules={[{ required: true, message: "Sukunimi on pakollinen." }]}
                            >
                                <Input />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={[12, 0]}>
                        <Col xs={24} md={12}>
                            <Form.Item
                                label="Sähköposti"
                                name="email"
                                rules={[
                                    { required: true, message: "Sähköpostiosoite on pakollinen." },
                                    { type: "email", message: "Tarkista sähköpostiosoitteen muoto." },
                                ]}
                            >
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item
                                label="Puhelinnumero"
                                name="phonenumber"
                                rules={[{ required: true, message: "Puhelinnumero on pakollinen." }]}
                            >
                                <Input />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={[12, 0]}>
                        <Col xs={24}>
                            <Form.Item label="Osoite" name="address">
                                <Input />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={[12, 0]}>
                        <Col xs={24} md={4}>
                            <Form.Item label="Postinumero" name="zipcode">
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={10}>
                            <Form.Item label="Kaupunki" name="city">
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={10}>
                            <Form.Item label="Maa" name="country">
                                <Input />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item
                        label="Käyttäjätyyppi"
                        name="type"
                        rules={[{ required: true, message: "Valitse käyttäjätilin tyyppi" }]}
                        initialValue="person"
                    >
                        <Radio.Group onChange={onUserTypeChange} buttonStyle="solid">
                            <Radio.Button value="person">Yksityishenkilö</Radio.Button>
                            <Radio.Button value="company">Yritys</Radio.Button>
                        </Radio.Group>
                    </Form.Item>

                    {showCompanyFields && (
                        <Row gutter={[12, 0]}>
                            <Col xs={24} md={12}>
                                <Form.Item
                                    label="Yrityksen nimi"
                                    name="companyName"
                                    rules={[{ required: true, message: "Yrityksen nimi on pakollinen." }]}
                                >
                                    <Input />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                                <Form.Item
                                    label="Y-tunnus"
                                    name="businessId"
                                    rules={[{ required: true, message: "Yrityksen Y-tunnus on pakollinen." }]}
                                >
                                    <Input />
                                </Form.Item>
                            </Col>
                        </Row>
                    )}

                    <div className="mb-5">
                        Mikäli haluat ilmoituksia esim. päättyneistä huutokaupoista tai ylitetyistä tarjouksistasi salli
                        haluamasi viestintäkanavat.
                    </div>

                    <Row gutter={[12, 0]} className="mb-5">
                        <Col xs={24} md={12}>
                            <Form.Item name="emailAllowed" valuePropName="checked">
                                <Checkbox>Sähköposti</Checkbox>
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item name="smsAllowed" valuePropName="checked">
                                <Checkbox>Tekstiviesti</Checkbox>
                            </Form.Item>
                        </Col>
                    </Row>

                    {!isAuthenticated && (
                        <>
                            <Row gutter={[12, 0]}>
                                <Col xs={24} md={12}>
                                    <Form.Item
                                        label="Salasana"
                                        name="password"
                                        rules={[{ required: true, message: "Syötä salasana!" }]}
                                        hasFeedback
                                    >
                                        <Input.Password />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={12}>
                                    <Form.Item
                                        label="Vahvista salasana"
                                        name="confirm"
                                        dependencies={[["password"]]}
                                        hasFeedback
                                        rules={[
                                            { required: true, message: "Vahvista salasana" },
                                            ({ getFieldValue }) => ({
                                                validator(_, value) {
                                                    if (!value || getFieldValue("password") === value) {
                                                        return Promise.resolve();
                                                    }
                                                    return Promise.reject(
                                                        new Error("The two passwords that you entered do not match!")
                                                    );
                                                },
                                            }),
                                        ]}
                                    >
                                        <Input.Password />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </>
                    )}

                    <Form.Item>
                        <Button type="primary" htmlType="submit" block>
                            {isAuthenticated ? "Tallenna" : "Rekisteröidy"}
                        </Button>
                    </Form.Item>
                </Form>
            )}
        </>
    );
};

export default ProfileForm;
